import * as React from 'react'
import {PRIMARY_BLUE, BORDER, withOpacity} from '../../../../styles/foundation/colors'
import {toTriple} from '../weightMath'

/**
 * Shared isometric carton outline — the SHAPE-ONLY drawing.
 *
 * It normalises the LARGEST of the three dimensions (`s = size * 0.62 / max(l,b,h)`),
 * so a 20 cm cube and a 60 cm cube are drawn at exactly the same size. What
 * survives the normalisation is proportion — long and flat vs tall and narrow —
 * and nothing else. That is deliberate: this is the drawing a screen reaches
 * for when it wants to say "this is roughly what that material looks like"
 * beside a name, not beside a measurement.
 *
 * Because of that, the aria-label says "Approximate carton", and it must keep
 * saying so. **Never place this next to a real measured L x B x H triple**
 * where a reader could take the outline as to-scale; if both have to share a
 * screen, this one is the one that carries the "approximate" label.
 *
 * When a screen genuinely needs the outline to grow with the numbers — the
 * editing feedback in Packing's pack drawer — it uses
 * `packing/components/CartonFigure.js`, which holds a constant pixels-per-
 * centimetre against a 60 cm reference. The two scaling rules are incompatible
 * and are deliberately kept in two files rather than behind a `variant` prop:
 * one component with a switch is how a screen ends up silently rendering with
 * the other rule's geometry.
 *
 * `{dims, size = 132}` is a contract — Initiate and Requests import this exact
 * signature. Do not rename or add required props.
 *
 * @param {number[]|{l:number,b:number,h:number}} dims  length, breadth, height in centimetres
 * @param {number}   size  the longest edge the drawing is allowed to occupy, in px
 */
export default function CartonSVG({dims, size = 132}) {
  // A carton with a missing or non-positive side has no shape to convey, and a
  // zero-width rectangle is worse than nothing — the caller renders its own
  // "dimensions missing" copy instead.
  const triple = toTriple(dims)
  if (!triple || triple.length !== 3) return null
  const [l, b, h] = triple.map(Number)
  if (!(l > 0) || !(b > 0) || !(h > 0)) return null

  const big = Math.max(l, b, h) || 1
  const s = (size * 0.62) / big
  const W = l * s
  const H = h * s
  const D = b * s * 0.5 // the isometric depth foreshortening
  const pad = 14
  const w = W + D + pad * 2
  const ht = H + D + pad * 2

  return (
    <svg
      viewBox={`0 0 ${w} ${ht}`}
      width='100%'
      height={Math.min(ht, size)}
      role='img'
      aria-label={`Approximate carton, ${l} by ${b} by ${h} centimetres`}
    >
      <g transform={`translate(${pad},${pad + D})`}>
        {/* top */}
        <polygon
          points={`0,0 ${D},${-D} ${W + D},${-D} ${W},0`}
          fill={withOpacity(PRIMARY_BLUE, 0.28)} stroke={PRIMARY_BLUE} strokeWidth='1.2'
        />
        {/* right side */}
        <polygon
          points={`${W},0 ${W + D},${-D} ${W + D},${H - D} ${W},${H}`}
          fill={withOpacity(PRIMARY_BLUE, 0.14)} stroke={PRIMARY_BLUE} strokeWidth='1.2'
        />
        {/* front face */}
        <rect
          x='0' y='0' width={W} height={H}
          fill={withOpacity(PRIMARY_BLUE, 0.08)} stroke={PRIMARY_BLUE} strokeWidth='1.2'
        />
        {/* tape seam — the one cue that reads "carton" rather than "cuboid" */}
        <line x1={W * 0.5} y1='0' x2={W * 0.5} y2={H} stroke={BORDER} strokeWidth='2' />
      </g>
    </svg>
  )
}
